import React, { useRef, useEffect, useCallback } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let googleScriptPromise = null;

function loadGoogleScript() {
    if (googleScriptPromise) return googleScriptPromise;

    googleScriptPromise = new Promise((resolve) => {
        if (window.google && window.google.maps && window.google.maps.places) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=fr`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        document.head.appendChild(script);
    });

    return googleScriptPromise;
}

// CSS + fix for Google autocomplete dropdown inside modals
const styleId = 'google-pac-style';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
        .pac-container {
            z-index: 10000 !important;
            pointer-events: auto !important;
        }
    `;
    document.head.appendChild(style);

    // Prevent modal from stealing clicks on Google autocomplete suggestions
    document.addEventListener('mousedown', (e) => {
        if (e.target.closest('.pac-container')) {
            e.stopPropagation();
        }
    }, true);

    document.addEventListener('touchstart', (e) => {
        if (e.target.closest('.pac-container')) {
            e.stopPropagation();
        }
    }, true);
}

export function AddressAutocompleteInput({ value, onChange, placeholder, className }) {
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (!GOOGLE_MAPS_API_KEY || !inputRef.current || autocompleteRef.current) return;

        loadGoogleScript().then(() => {
            if (!inputRef.current || autocompleteRef.current) return;

            const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
                types: ['address'],
                componentRestrictions: { country: 'fr' },
                fields: ['formatted_address'],
            });

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place && place.formatted_address) {
                    onChangeRef.current(place.formatted_address);
                }
            });

            autocompleteRef.current = autocomplete;
        });

        return () => {
            if (autocompleteRef.current && window.google) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
                autocompleteRef.current = null;
            }
        };
    }, []);

    const handleChange = useCallback((e) => {
        onChangeRef.current(e.target.value);
    }, []);

    return (
        <input
            ref={inputRef}
            value={value || ''}
            onChange={handleChange}
            placeholder={placeholder || "Saisir l'adresse..."}
            className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className || ''}`}
            data-testid="address-autocomplete"
        />
    );
}
